import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { validateEmail, isSafeInput } from "@/lib/validation";

const contactInfo = [
  { icon: Mail, label: "Email", value: "secureflexgroup@outlook.com", href: "mailto:secureflexgroup@outlook.com" },
  { icon: Phone, label: "Phone", value: "+27 69 922 3141", href: "tel:+27699223141" },
  { icon: MapPin, label: "Office Address", value: "63 Assembly Road, Bluff, Durban, South Africa" },
  { icon: Clock, label: "Support Hours", value: "24/7 — around the clock" },
];

const ContactSection = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const firstName = (data.get("firstName") as string || "").trim();
    const lastName = (data.get("lastName") as string || "").trim();
    const email = (data.get("email") as string || "").trim();
    const subject = (data.get("subject") as string || "").trim();
    const message = (data.get("message") as string || "").trim();
    if (!firstName || !lastName || !email || !subject || !message) { toast.error("Please fill in all fields"); return; }
    if (!validateEmail(email)) { toast.error("Please enter a valid email address"); return; }
    if (!isSafeInput(firstName) || !isSafeInput(lastName) || !isSafeInput(subject) || !isSafeInput(message)) {
      toast.error("Invalid characters detected in your input. Please remove any special formatting or code.");
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("contact-form", { body: { firstName, lastName, email, subject, message } });
      if (error) { 
        console.error("Contact form invocation error:", error);
        let errorData;
        try {
          errorData = typeof error.message === 'string' ? JSON.parse(error.message) : error;
        } catch {
          errorData = error;
        }
        const detailedMsg = errorData.error || error.message || "Unknown error";
        toast.error(`Submission failed: ${detailedMsg}. Please try again.`); 
      } else if (data?.success) { 
        setSubmitted(true); 
        if (data.warning) {
          toast.warning(`Message received, but notification failed: ${data.warning}`);
        } else {
          toast.success("Message sent! We'll get back to you shortly."); 
        }
      } else {
        toast.error(`Submission failed: ${data?.error || "Unknown error"}`);
      }
    } catch (err: any) { 
      console.error("Submission error:", err);
      toast.error(`Something went wrong: ${err.message || "Network error"}. Please try again.`); 
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" className="py-24">
      <div className="mx-auto max-w-7xl px-6">
        <p className="text-sm font-semibold tracking-widest text-primary">Get in Touch</p>
        <h2 className="mt-2 text-3xl font-bold md:text-5xl">CONTACT US</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">Have questions about SecureFlex? Our team is ready to help you get started or answer any questions about our platform.</p>
        <div className="mt-12 grid gap-8 md:grid-cols-2">
          <div className="space-y-4">
            {contactInfo.map((item, i) => (
              <div key={item.label} className="flex items-start gap-4 rounded-xl border border-border bg-card p-5 animate-fade-in" style={{ animationDelay: `${i * 0.1}s`, animationFillMode: "backwards" }}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{item.label}</p>
                  {item.href ? (<a href={item.href} className="text-sm text-primary hover:underline">{item.value}</a>) : (<p className="text-sm text-muted-foreground">{item.value}</p>)}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-border bg-card p-8 animate-fade-in">
            {submitted ? (
              <div className="py-12 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10"><Send className="h-6 w-6 text-primary" /></div>
                <h3 className="text-xl font-bold text-primary">Message Sent!</h3>
                <p className="mt-2 text-sm text-muted-foreground">We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-bold">Send us a message</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input name="firstName" placeholder="First Name" required maxLength={100} className="rounded-lg border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary transition-colors" />
                  <input name="lastName" placeholder="Last Name" required maxLength={100} className="rounded-lg border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary transition-colors" />
                </div>
                <input name="email" placeholder="Email Address" type="email" required maxLength={255} className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary transition-colors" />
                <input name="subject" placeholder="Subject" required maxLength={200} className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary transition-colors" />
                <textarea name="message" placeholder="Your message..." rows={4} required maxLength={2000} className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary transition-colors resize-none" />
                <Button type="submit" className="w-full gap-2 rounded-full" disabled={loading}>
                  {loading ? (<span className="flex items-center gap-2"><span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />Sending...</span>) : (<><Send className="h-4 w-4" /> Send Message</>)}
                </Button>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
